import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { EiqButton } from 'eiq-storybook';

import { getPreSignedUrl } from 'src/utils/helpers';
import { notification } from 'src/components/Notification';
import { useCreateJobAuditSiteData, useDeleteJobPhotoRecords, useUpdateJobAuditSiteData } from 'src/hooks/job';
import Button from 'src/components/Button';
import ButtonTag from 'src/components/ButtonTag';
import Loader from 'src/components/Loader';
import logger from 'src/utils/logger';
import MobilePhotoTagSelectorPopup from 'src/components/JobMobileDataTab/MobilePhotoTagSelectorPopup';
import MobilePhotoQuestionManagePopup from 'src/components/MobilePhotoQuestionManagePopup';
import Styled from 'src/components/MobilePhotoAttachmentViewer/MobilePhotoAttachmentViewer.styles';
import ImageEditing from '../ImageEditing';

const MobilePhotoAttachmentViewer = (props) => {
  const {
    allPhotoRecords,
    closeHandler,
    getPhotoRecordsFromDB,
    isEditableByUser,
    isJobUnderReview,
    jobChecklistsQuestions,
    jobId,
    photoRecords,
    clickedImageIndex,
    showLinks,
    tagsConfiguration,
    setAlteredImageData,
    jobReportNumber,
    revertEditedImgToOriginal,
  } = props;

  const [galleryData, setGalleryData] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(clickedImageIndex);
  const [isAddingTags, setIsAddingTags] = useState(false);
  const [loadingImage, setLoadingImage] = useState(false);
  const [isManagingQuestions, setIsManagingQuestions] = useState(false);
  const [linkingOrRemovingQuestions, setLinkingOrRemovingQuestions] = useState(false);
  const [showDeleteConfirmPopup, setShowDeleteConfirmPopup] = useState(false);
  const [enablePhotoEditing, setEnablePhotoEditing] = useState(false);

  const { t } = useTranslation();

  const tags = useMemo(() => {
    if (galleryData[selectedImageIndex]?.isPrimary) {
      return galleryData[selectedImageIndex]?.tags;
    }
    return allPhotoRecords?.find(record => (
      record.filename === galleryData[selectedImageIndex]?.filename && record.isPrimary
    ))?.tags;

  }, [selectedImageIndex, galleryData]);

  const hasTags = useMemo(() => {
    return tags?.filter(({ name }) => Boolean(name)).length !== 0;
  }, [selectedImageIndex, galleryData]);

  const updateJobAuditSiteData = useUpdateJobAuditSiteData();

  useEffect(() => {
    if (photoRecords && photoRecords.length > 0) {
      setGalleryData(photoRecords);
    }
  }, [photoRecords]);

  const assignImageSrc = async (selectedPhotoRecord, thumbnailType, requestOptions = {})=>{
    const jobAttachmentId = selectedPhotoRecord.showAltered
      ? selectedPhotoRecord.alteredAttachmentId
      : selectedPhotoRecord.attachmentId;
    if(thumbnailType) {
      return getPreSignedUrl(jobAttachmentId, null, 'md', requestOptions);
    }
    return getPreSignedUrl(jobAttachmentId, null, undefined, requestOptions);
  };

  const [currentSrc, setCurrentSrc] = useState();

  const setImageUrl = async (force = false) => {
    try {
      const { showAltered } = galleryData[selectedImageIndex];
      if (!force && currentSrc?.index === selectedImageIndex && currentSrc?.showAltered === showAltered) {
        return;
      }

      setLoadingImage(true);

      const src = showAltered
        ? await assignImageSrc(galleryData[selectedImageIndex], undefined, { fetchPolicy: 'network-only' })
        : await assignImageSrc(galleryData[selectedImageIndex], 'md', { fetchPolicy: 'network-only' });

      setCurrentSrc({
        index: selectedImageIndex,
        showAltered,
        src,
      });
    } catch (err) {
      logger.error(err);
    } finally {
      setLoadingImage(false);
    }
  };
  useEffect(() => {
    if (galleryData && galleryData.length > 0) {
      setImageUrl();
    }
  }, [selectedImageIndex, galleryData]);
  useEffect(() => {
    setImageUrl(true);
  }, [enablePhotoEditing]);

  const deletePhotoTag = async (id, name) => {
    try {
      const photoRecord = { ...galleryData[selectedImageIndex] };
      const { data: { UpdateJobAuditSiteData: { success } } } = await updateJobAuditSiteData(jobId, {
        photoRecords: [{
          id: photoRecord.id,
          order: parseInt(photoRecord.order, 10),
          viewInPhotoForm: photoRecord.viewInPhotoForm,
          showFindings: photoRecord.showFindings,
          showAltered: photoRecord.showAltered,
          alteredFileName: photoRecord.alteredFileName,
          alteredAttachmentId: photoRecord.alteredAttachmentId,
          photoTagsRecords: [{
            id,
            isDeleted: true,
            name,
          }],
        }],
      });
      if (!success) {
        notification.error(t('TAG_REMOVAL_FAILED_ERR_MSG'));
      }
      await getPhotoRecordsFromDB();
    } catch (e) {
      notification.error(t('TAG_REMOVAL_FAILED_ERR_MSG'));
    }
  };

  const createRoundedSpan = ({ id, name }) => <ButtonTag key={id}
                                                         crossIconClickHandler={() => deletePhotoTag(id, name)}
                                                         showCrossIcon={isEditableByUser && isJobUnderReview}
                                                         text={name} />;

  const getDescriptionOrTags = (imageGalleryItem) => {
    return tags?.map(tag => ({
      id: tag.id,
      remoteId: tag.remoteId,
      name: tag.name,
    }));
  };

  const navigateToNext = (e) => {
    e.preventDefault();
    setSelectedImageIndex(current => current === galleryData.length - 1 ? 0 : current + 1);
  };

  const navigateToPrevious = (e) => {
    e.preventDefault();
    setSelectedImageIndex(current => current === 0 ? galleryData.length - 1 : current - 1);
  };

  const getExistingTagsToCompare = () => {
    return (galleryData[selectedImageIndex]?.tags || [])
      .map(tag => ({ tagId: tag.tagId, name: tag.name }));
  };

  const addTagsToImage = async (selectedTags) => {
    try {
      const photoRecord = { ...galleryData[selectedImageIndex] };
      const { data: { UpdateJobAuditSiteData: { success } } } = await updateJobAuditSiteData(jobId, {
        photoRecords: [{
          id: photoRecord.id,
          order: parseInt(photoRecord.order, 10),
          viewInPhotoForm: photoRecord.viewInPhotoForm,
          showFindings: photoRecord.showFindings,
          showAltered: photoRecord.showAltered,
          alteredFileName: photoRecord.alteredFileName,
          alteredAttachmentId: photoRecord.alteredAttachmentId,
          photoTagsRecords: selectedTags.map(tag => ({
            name: tag.label,
            tagId: tag.value,
            version: tag.version,
          })),
        }],
      });

      if (!success) {
        notification.error(t('TAG_ADDITION_FAILED_ERR_MSG'));
      }
    } catch (err) {
      notification.error(t('TAG_ADDITION_FAILED_ERR_MSG'));
    } finally {
      setIsAddingTags(false);
      await getPhotoRecordsFromDB();
    }
  };

  const missingTagsConfigurationTagIds = useMemo(() => {
    if (!isAddingTags) {
      return [];
    }

    const requiredTags = tagsConfiguration?.currentVersion?.tagsConfigurationTags || [];
    if (requiredTags.length === 0) {
      return [];
    }

    const requiredEssentialTagIds = requiredTags.filter(c => c.requirement === 'essential').map(c => c.tagId);

    const usedTags = galleryData.map(c => c.tags || []).flat(1).map(c => c.tagId);

    return requiredEssentialTagIds.filter(tagId => !usedTags.includes(tagId));
  }, [isAddingTags, galleryData]);

  const createJobAuditSiteData = useCreateJobAuditSiteData();
  const deleteJobMobilePhotoRecords = useDeleteJobPhotoRecords();

  const linkSelectedQuestions = async (selectedQuestions = [], questionsToBeRemoved = []) => {
    const newQuestions = selectedQuestions.filter(question => !questionsToBeRemoved.find(q => q.uniqueId === question.uniqueId));
    const photoRecord = galleryData[selectedImageIndex];
    setLinkingOrRemovingQuestions(true);
    try {
      const newMobileData = {
        photoRecords: newQuestions.map(question => {
          return {
            filename: photoRecord.filename,
            checklistId: question.checklistId,
            questionId: question.questionId,
          };
        }), geolocationsRecords: [],
      };

      await createJobAuditSiteData(jobId, newMobileData);

      await deleteJobMobilePhotoRecords(jobId, allPhotoRecords.filter(record => {
        return record.filename === photoRecord.filename &&
          !!questionsToBeRemoved.find(question => {
            return record.questionId === question.questionId &&
              record.checklistId === question.checklistId;
          });
      }).map(record => parseInt(record.id,10)));
    } catch (err) {
      notification.error('Failed to link or remove one or more questions to mobile data tab image', err);
    } finally {
      setLinkingOrRemovingQuestions(false);
      setIsManagingQuestions(false);
      await getPhotoRecordsFromDB();
    }
  };

  const removeMobilePhoto = async (photoRecord) =>{
    try {
      const { data: { DeletePhotoRecords: { success } } } = await deleteJobMobilePhotoRecords(jobId, allPhotoRecords.filter(record => {
        return record.filename === photoRecord.filename;
      }).map(record => parseInt(record.id,10)), photoRecord.isPrimary);
      if (success) {
        notification.success(t('MOBILE_PHOTO_RECORD_DELETE_SUCCESS'));
      }else{
        notification.error(t('MOBILE_PHOTO_RECORD_DELETE_ERROR'));
      }
    } catch (err) {
      notification.error(t('MOBILE_PHOTO_RECORD_DELETE_ERROR'), err);
    } finally {
      setShowDeleteConfirmPopup(false);
      await getPhotoRecordsFromDB();
      setSelectedImageIndex(current => current === galleryData.length - 1 ? 0 : current);
      if(photoRecords.length === 1){
        closeHandler();
      }
    }
  }


  const renderDeletePhotoConfirmPopup = () => {
    return (
      <Styled.PhotoDeleteConfirmPopup isCloseHidden={false} onClose={() => setShowDeleteConfirmPopup(false)}>
        <p>
          {t('DELETE_MOBILE_PHOTO_WARNING')}
        </p>
        <Styled.PhotoDeletePopupActionBtnContainer>
          <EiqButton
            variant='btn--solid' 
            variantColor='mint-100'
            variantTextColor='text-black'
            className='deleteConfirmBtn'
            label={t('OK')}
            onClick={()=>removeMobilePhoto(galleryData[selectedImageIndex])}
          />
          <EiqButton
            variant='btn--outline-link'
            label={t('CANCEL')}
            onClick={() => setShowDeleteConfirmPopup(false)}
          />
        </Styled.PhotoDeletePopupActionBtnContainer>
      </Styled.PhotoDeleteConfirmPopup>
    )
  }

  return (
    <Styled.Container>
      {enablePhotoEditing
        ? <ImageEditing
          filename={galleryData[selectedImageIndex].filename}
          getImagePath={assignImageSrc}
          jobId={jobId}
          jobReportNumber={jobReportNumber}
          photoAttachmentId={galleryData[selectedImageIndex].id}
          refreshPhotoRecords={getPhotoRecordsFromDB}
          selectedPhotoRecord={galleryData[selectedImageIndex]}
          setAlteredImageData={setAlteredImageData}
          setEnablePhotoEditing={setEnablePhotoEditing}
        />
        : <>
       {showDeleteConfirmPopup && renderDeletePhotoConfirmPopup()}
      <Styled.Icon close name="cross" onClick={closeHandler} />
      {galleryData && galleryData.length > 0 ?
        <>
          <Styled.ButtonContainer prev>
            <Button onClick={(e) => navigateToPrevious(e)}>{t('PREVIOUS_MOBILE_PHOTO_PREVIEW')}</Button>
          </Styled.ButtonContainer>
          <Styled.ImageBox>
            {loadingImage ?
              <Loader style={{ color: 'white', }} withText={true} /> :
              <Styled.Image single-data-row-no_edit src={currentSrc?.src} />
            }
          </Styled.ImageBox>
          <Styled.ButtonContainer next>
            <Button onClick={(e) => navigateToNext(e)}>{t('NEXT_MOBILE_PHOTO_PREVIEW')}</Button>
          </Styled.ButtonContainer>

          <Styled.DataRowContainer single-data-row-no_edit>
            <Styled.DataRow>
              {t('PHOTO_ATTRIBUTES')}
              <Styled.DataCellItem>
                {hasTags ?
                  <>
                    <Styled.TagsRowLabel>{t('TAGS')}:</Styled.TagsRowLabel>
                    <Styled.DescriptionContainer expand={true}>
                      {
                        getDescriptionOrTags(galleryData[selectedImageIndex])
                          .map(item => createRoundedSpan(item))
                      }
                    </Styled.DescriptionContainer>
                  </>
                  : null
                }
                {(isEditableByUser && isJobUnderReview) &&(
                  <>
                  <Styled.ActionBtnContainer onClick={() => setIsAddingTags(true)}>
                    <Styled.ActionButton name="plus" />
                    &nbsp; {t('TAGS_ADD_FOR_MOBILE_PHOTO')}
                  </Styled.ActionBtnContainer>

                  <Styled.ActionBtnContainer remove={true}>
                    <Button red solid type="button" onClick={() => setShowDeleteConfirmPopup(true)}>
                      {t('DELETE')} &nbsp;
                      <Styled.ActionButton name="remove" />
                    </Button>
                  </Styled.ActionBtnContainer>
                  </>
                )
                }
              </Styled.DataCellItem>
              {showLinks &&
                <>
                  <Styled.DataCellItem linked-questions-row>
                    <Styled.ActionBtnContainer onClick={() => setIsManagingQuestions(true)}>
                      <Styled.TagsRowLabel linked-questions>{t('LINKS')}:</Styled.TagsRowLabel>
                      &nbsp;
                      <Styled.ManageQuestionsBtn name="pencil" />
                    </Styled.ActionBtnContainer>
                  </Styled.DataCellItem>
                </>
              }
            </Styled.DataRow>
            {isEditableByUser && (
              <Styled.DataRow>
                {t('PHOTO_TOOLS')}
                <Styled.DataCellItem>
                  <Styled.ActionBtnContainer onClick={()=> {
                    setEnablePhotoEditing(!enablePhotoEditing);
                  }}>
                    <Styled.ActionButton name="pencil" path-blue/>
                    &nbsp;{t('EDIT_PHOTO')}
                  </Styled.ActionBtnContainer>
                </Styled.DataCellItem>
                {galleryData[selectedImageIndex]?.showAltered
                  ? <Styled.DataCellItem>
                    <Styled.ActionBtnContainer
                      onClick={() => {
                        revertEditedImgToOriginal(galleryData[selectedImageIndex]);
                        getPhotoRecordsFromDB();
                      }}>
                      <Styled.ActionButton name="reload" reload />
                      &nbsp;{t('REVERT_TO_ORIGINAL_PHOTO')}
                    </Styled.ActionBtnContainer>
                  </Styled.DataCellItem>
                  : null}
              </Styled.DataRow>
            )}
          </Styled.DataRowContainer>
        </>
        : null
      }
      {isAddingTags ? <MobilePhotoTagSelectorPopup
        existingTags={getExistingTagsToCompare()}
        missingTagsConfigurationTagIds={missingTagsConfigurationTagIds}
        onCloseHandler={() => setIsAddingTags(false)}
        saveAndApplyHandler={addTagsToImage} /> : null}

      {isManagingQuestions ? <MobilePhotoQuestionManagePopup
        allPhotoRecords={allPhotoRecords}
        jobChecklistsQuestions={jobChecklistsQuestions}
        jobId={jobId}
        linkingOrRemovingQuestions={linkingOrRemovingQuestions}
        onCloseHandler={() => setIsManagingQuestions(false)}
        readOnly={!(isEditableByUser && isJobUnderReview)}
        saveAndApplyHandler={linkSelectedQuestions}
        selectedRecord={galleryData[selectedImageIndex]}
      /> : null}
      </>}
    </Styled.Container>
  );
};

MobilePhotoAttachmentViewer.propTypes = {
  allPhotoRecords: PropTypes.array.isRequired,
  closeHandler: PropTypes.func.isRequired,
  getPhotoRecordsFromDB: PropTypes.func.isRequired,
  isEditableByUser: PropTypes.bool.isRequired,
  isJobUnderReview: PropTypes.bool.isRequired,
  jobId: PropTypes.number.isRequired,
  jobReportNumber: PropTypes.string.isRequired,
  photoRecords: PropTypes.array.isRequired,
  revertEditedImgToOriginal: PropTypes.func.isRequired,
  setAlteredImageData: PropTypes.func.isRequired,
  tagsConfiguration: PropTypes.object.isRequired,
  clickedImageIndex: PropTypes.number,
  jobChecklistsQuestions: PropTypes.array,
  showLinks: PropTypes.bool,
};

MobilePhotoAttachmentViewer.defaultProps = {
  clickedImageIndex: 0,
  jobChecklistsQuestions: [],
  showLinks: true,
};

export default React.memo(MobilePhotoAttachmentViewer);
